import React from 'react';

import { hasMarkerRegionChangedEnough, regionsEqual } from '@components/Explorer/MapGeometry';
import type { MapRegion } from '@helpers/Map';

export type MapRegionChangeMeta = {
  userInteraction: boolean;
};

type MapRegionChangeDetails = {
  isGesture?: boolean;
};

type ExplorerMapRegionControllerOptions = {
  initialMapRegion: MapRegion;
  mapFocusRegion?: MapRegion;
  onMapRegionChange?: (region: MapRegion, meta: MapRegionChangeMeta) => void;
};

type ExplorerMapRegionController = {
  mapRegion: MapRegion;
  focusMapRegion: (region: MapRegion) => void;
  handleMapInteraction: () => void;
  handleMapRegionChangeComplete: (region: MapRegion, details?: MapRegionChangeDetails) => void;
};

export function useExplorerMapRegionController({
  initialMapRegion,
  mapFocusRegion,
  onMapRegionChange,
}: ExplorerMapRegionControllerOptions): ExplorerMapRegionController {
  const [mapRegion, setMapRegion] = React.useState<MapRegion>(initialMapRegion);
  const hasUserControlledMapRegionRef = React.useRef(false);
  const pendingUserMapInteractionRef = React.useRef(false);
  const pendingControlledMapRegionRef = React.useRef<MapRegion | undefined>(undefined);
  const ignoredControlledMapCompletionRegionRef = React.useRef<MapRegion | undefined>(undefined);
  const onMapRegionChangeRef = React.useRef(onMapRegionChange);

  const updateMapRegion = React.useCallback(
    ({ region, userInteraction = false }: { region: MapRegion; userInteraction?: boolean }) => {
      if (userInteraction) {
        hasUserControlledMapRegionRef.current = true;
      }

      setMapRegion((currentRegion) => {
        if (!hasMarkerRegionChangedEnough(currentRegion, region)) return currentRegion;

        return region;
      });
      onMapRegionChangeRef.current?.(region, { userInteraction });
    },
    []
  );

  const focusControlledMapRegion = React.useCallback(
    ({ region, userInteraction }: { region: MapRegion; userInteraction: boolean }) => {
      hasUserControlledMapRegionRef.current = true;
      pendingUserMapInteractionRef.current = false;
      pendingControlledMapRegionRef.current = region;
      ignoredControlledMapCompletionRegionRef.current = undefined;
      setMapRegion((currentRegion) =>
        regionsEqual(currentRegion, region) ? currentRegion : region
      );
      onMapRegionChangeRef.current?.(region, { userInteraction });
    },
    []
  );

  const handleMapInteraction = React.useCallback(() => {
    hasUserControlledMapRegionRef.current = true;
    pendingUserMapInteractionRef.current = true;
    pendingControlledMapRegionRef.current = undefined;
    ignoredControlledMapCompletionRegionRef.current = undefined;
  }, []);

  const handleMapRegionChangeComplete = React.useCallback(
    (region: MapRegion, details?: MapRegionChangeDetails) => {
      const ignoredControlledMapCompletionRegion = ignoredControlledMapCompletionRegionRef.current;
      if (
        ignoredControlledMapCompletionRegion &&
        !hasMarkerRegionChangedEnough(ignoredControlledMapCompletionRegion, region)
      ) {
        return;
      }

      ignoredControlledMapCompletionRegionRef.current = undefined;

      const pendingControlledMapRegion = pendingControlledMapRegionRef.current;
      if (pendingControlledMapRegion) {
        if (hasMarkerRegionChangedEnough(pendingControlledMapRegion, region)) {
          pendingControlledMapRegionRef.current = undefined;
          ignoredControlledMapCompletionRegionRef.current = region;
          return;
        }

        pendingControlledMapRegionRef.current = undefined;
        updateMapRegion({ region, userInteraction: false });
        return;
      }

      const userInteraction = details?.isGesture === true || pendingUserMapInteractionRef.current;
      pendingUserMapInteractionRef.current = false;
      updateMapRegion({ region, userInteraction });
    },
    [updateMapRegion]
  );

  const focusMapRegion = React.useCallback(
    (region: MapRegion) => {
      focusControlledMapRegion({ region, userInteraction: true });
    },
    [focusControlledMapRegion]
  );

  React.useEffect(() => {
    onMapRegionChangeRef.current = onMapRegionChange;
  }, [onMapRegionChange]);

  React.useEffect(() => {
    if (hasUserControlledMapRegionRef.current) return;

    pendingUserMapInteractionRef.current = false;
    pendingControlledMapRegionRef.current = undefined;
    ignoredControlledMapCompletionRegionRef.current = undefined;
    setMapRegion((currentRegion) =>
      regionsEqual(currentRegion, initialMapRegion) ? currentRegion : initialMapRegion
    );
    onMapRegionChangeRef.current?.(initialMapRegion, { userInteraction: false });
  }, [initialMapRegion]);

  React.useEffect(() => {
    if (!mapFocusRegion) return;

    // mapFocusRegion is an imperative focus command from the parent route/search UI.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    focusControlledMapRegion({ region: mapFocusRegion, userInteraction: false });
  }, [focusControlledMapRegion, mapFocusRegion]);

  return {
    mapRegion,
    focusMapRegion,
    handleMapInteraction,
    handleMapRegionChangeComplete,
  };
}
